/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { ServiceIconType } from "@/api/services";
import { SERVICE_ICONS, SERVICE_ICON_KEYS } from "@/constants/serviceIcons";

interface IconPickerProps {
  iconType: ServiceIconType;
  iconValue: string;
  onChange: (iconType: ServiceIconType, iconValue: string) => void;
}

export const IconPicker: React.FC<IconPickerProps> = ({
  iconType,
  iconValue,
  onChange,
}) => {
  const [search, setSearch] = useState("");

  const filteredKeys = SERVICE_ICON_KEYS.filter((key) =>
    SERVICE_ICONS[key].label.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant={iconType === "preset" ? "default" : "secondary"}
          size="sm"
          onClick={() => onChange("preset", iconValue)}
        >
          Preset icon
        </Button>
        <Button
          type="button"
          variant={iconType === "url" ? "default" : "secondary"}
          size="sm"
          onClick={() => onChange("url", "")}
        >
          Image URL
        </Button>
      </div>

      {iconType === "preset" ? (
        <div className="space-y-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search icons..."
          />
          <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto p-1">
            {filteredKeys.map((key) => {
              const { label, Icon } = SERVICE_ICONS[key];
              const selected = iconValue === key;
              return (
                <button
                  key={key}
                  type="button"
                  title={label}
                  onClick={() => onChange("preset", key)}
                  className={cn(
                    "flex items-center justify-center h-10 w-10 rounded-lg border transition-colors",
                    selected
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                      : "border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800",
                  )}
                >
                  <Icon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                </button>
              );
            })}
            {filteredKeys.length === 0 && (
              <p className="col-span-6 text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                No matching icons
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="service-icon-url">Image URL</Label>
          <Input
            id="service-icon-url"
            value={iconValue}
            onChange={(e) => onChange("url", e.target.value)}
            placeholder="https://example.com/icon.png"
          />
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>Preview:</span>
            {iconValue ? (
              <img
                src={iconValue}
                alt=""
                className="h-6 w-6 rounded object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <GlobeAltIcon className="h-5 w-5" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
